import { twMerge } from '@panwds/tailwindcss';
import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
/**
 *
 * ### Import Guide
 *
 * ```jsx
 * import { ViewSwitcher} from "@panwds/react-ui";
 * ```
 */
export const ViewSwitcher = forwardRef(({ animationDuration = 280, dataMetrics, dataTestId = 'view-switcher', views, defaultSelected, onChange, className }, ref) => {
    /**
     * State and refs:
     * - currentView: Tracks the currently selected view. Initialized with defaultSelected if provided, otherwise with the first view's value.
     * - activeRef: Reference to the active view element for DOM manipulation.
     * - viewContainer: Reference to the view container element for layout calculations.
     */
    const [activeView, setActiveView] = useState(defaultSelected || views[0].value);
    const activeRef = useRef(null);
    const viewContainer = useRef(null);
    useLayoutEffect(() => {
        updateActiveState(activeView);
    }, [activeView, viewContainer, activeRef, views]);
    const handleChange = useCallback(({ label, value }) => {
        setActiveView(value);
        // Trigger callback for parent component
        onChange?.({ label, value });
    }, [onChange, setActiveView]);
    const updateActiveState = useCallback((value) => {
        const container = viewContainer.current;
        const buttons = container?.querySelectorAll('.btn-slide');
        const index = views.findIndex((v) => v.value === value);
        const button = buttons?.[index];
        const activeState = activeRef.current;
        const containerBounding = container?.getBoundingClientRect();
        const buttonBounding = button?.getBoundingClientRect();
        if (containerBounding && buttonBounding && activeState) {
            activeState.style.left = `${Math.round(buttonBounding.left - containerBounding.left)}px`;
            activeState.style.width = `${buttonBounding.width - 2}px`;
            activeState.style.opacity = '1';
        }
    }, [viewContainer, views, activeRef]);
    return (React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId, className: twMerge('twds-relative twds-z-10 twds-h-8 twds-w-full', className?.container), ref: ref },
        React.createElement("div", { className: twMerge('twds-p-0.5 twds-absolute twds-inset-0 twds-left-1 twds-flex  twds-w-fit twds-h-7 twds-mx-auto twds-bg-gray-200 dark:twds-bg-gray-1000 twds-rounded-full twds-gap-0.5', className?.viewContainer), ref: viewContainer, "aria-label": "View options", role: "radiogroup" },
            React.createElement("span", { className: twMerge('twds-absolute twds-h-6 twds-rounded-full twds-top-[3px] twds-transition-left twds-ease-out twds-border twds-border-solid twds-border-blue-600 dark:twds-border-gray-500 twds-bg-white dark:twds-bg-gray-1000', className?.activeIndicator), style: { transitionDuration: `${animationDuration}ms` }, ref: activeRef, role: "presentation" }),
            views.map(({ label, value }, index) => (React.createElement("button", { key: index, role: "radio", tabIndex: index, "aria-checked": value === activeView, className: twMerge('twds-appearance-none twds-bg-transparent twds-font-lato twds-cursor-pointer twds-rounded-full twds-text-xs twds-px-3 twds-py-[5px] twds-font-semibold twds-z-[1] twds-mx-0.5', 'btn-slide twds-border twds-border-solid twds-border-transparent', value !== activeView
                    ? 'twds-text-gray-700 dark:twds-text-gray-400 hover:twds-border-gray-400 dark:hover:twds-border-gray-700'
                    : 'twds-pointer-events-none twds-text-primary dark:twds-text-primary-dark', className?.viewBtn), onKeyDown: (e) => e.key === 'Enter' && handleChange({ label, value }), onClick: () => handleChange({ label, value }), type: "button" }, label))))));
});
//# sourceMappingURL=ViewSwitcher.js.map